import { SCOPE_NAMES, scopedValue, defaultScope } from './scopes';

/**
 * How a term compares what it read against what it carries.
 *
 * Separate from the filter surface's `OPERATORS`, whose values travel to the API as query terms:
 * these are compared in the client and the two lists answer to different things.
 */
export const PREDICATE_OPERATORS = [
  { value: 'eq', label: 'EQUALS' },
  { value: 'neq', label: 'NOT_EQUALS' },
  { value: 'gt', label: 'GREATER_THAN' },
  { value: 'gte', label: 'GREATER_OR_EQUAL' },
  { value: 'lt', label: 'LESS_THAN' },
  { value: 'lte', label: 'LESS_OR_EQUAL' },
  { value: 'contains', label: 'CONTAINS' },
  { value: 'in', label: 'ONE_OF' },
  { value: 'set', label: 'IS_SET' },
  { value: 'unset', label: 'IS_NOT_SET' },
];

/** Whether a value counts as something rather than nothing. */
export const isSet = (value) =>
  value !== null &&
  value !== undefined &&
  value !== '' &&
  !(Array.isArray(value) && value.length === 0);

/** The pair as numbers where both read as numbers, as text otherwise. */
const comparable = (left, right) => {
  const pair = [Number(left), Number(right)];

  return pair.every((one) => !Number.isNaN(one) && one !== null)
    ? pair
    : [String(left ?? ''), String(right ?? '')];
};

/** An ordering operator, comparing the pair as `comparable` reads it. */
const ordered = (test) => (held, wanted) => test(...comparable(held, wanted));

/** What each operator makes of the value it read and the value it carries. */
const COMPARE = {
  eq: (held, wanted) => String(held ?? '') === String(wanted ?? ''),
  neq: (held, wanted) => String(held ?? '') !== String(wanted ?? ''),
  gt: ordered((a, b) => a > b),
  gte: ordered((a, b) => a >= b),
  lt: ordered((a, b) => a < b),
  lte: ordered((a, b) => a <= b),
  contains: (held, wanted) =>
    String(held ?? '')
      .toLowerCase()
      .includes(String(wanted ?? '').toLowerCase()),
  in: (held, wanted) =>
    (Array.isArray(wanted) ? wanted : [wanted]).some(
      (one) => String(one ?? '') === String(held ?? '')
    ),
  set: (held) => isSet(held),
  unset: (held) => !isSet(held),
};

/** The names of the groups a predicate may be, rather than a term. */
const GROUPS = ['all', 'any', 'not'];

/** Which group a predicate is, or null where it is a term. */
const groupOf = (predicate) =>
  GROUPS.find((name) => predicate?.[name] !== undefined) ?? null;

/**
 * Whether one term holds.
 *
 * A term naming a scope or an operator this build does not hold reads as true, so a layout written
 * against a later build shows its field rather than losing it. A scope the surface cannot offer is
 * read the same way: a list column is decided once for the whole list, so a term about the row it
 * would be drawn in has nothing to answer to.
 */
const termHolds = (term, scopes, allowed) => {
  const scope = term?.scope ?? defaultScope(scopes);

  if (!allowed.includes(scope)) {
    console.warn(`Unsupported binding scope: ${scope}`);

    return true;
  }

  const compare = COMPARE[term?.op];

  if (!compare) {
    console.warn(`Unsupported predicate operator: ${term?.op}`);

    return true;
  }

  return Boolean(compare(scopedValue(term, scopes), term?.value));
};

/**
 * Whether a predicate holds against what is in scope.
 *
 * A predicate is either a term - `{ scope, key, op, value }` - or one of three groups over other
 * predicates: `all`, `any` and `not`. Anything it cannot make sense of, an absent predicate
 * included, holds.
 *
 * @param {Object} predicate - The predicate, or nothing.
 * @param {Object} scopes - What is in scope, as `useBindingScopes` returns it.
 * @param {Array} allowed - The scopes this surface can answer for; all three where it does not say.
 *
 * @returns {Boolean}
 */
export const satisfies = (predicate, scopes, allowed = SCOPE_NAMES) => {
  if (predicate === null || predicate === undefined) return true;

  const group = groupOf(predicate);

  if (group === 'not') return !satisfies(predicate.not, scopes, allowed);

  if (group === 'all')
    return (predicate.all ?? []).every((one) =>
      satisfies(one, scopes, allowed)
    );

  if (group === 'any') {
    const terms = predicate.any ?? [];

    return (
      terms.length === 0 || terms.some((one) => satisfies(one, scopes, allowed))
    );
  }

  return termHolds(predicate, scopes, allowed);
};

export default satisfies;
